import type { ListParams } from '@/components/data/data-table';
import type { CursorPage } from '@/hooks/use-cursor-select';
import { apiClient } from '@/lib/api/client';
import { endpoints } from '@/lib/api/endpoints';

import { dealerDeviceToListQuery } from './device-filters';
import {
  dealerCityPageSchema,
  dealerDetailSchema,
  dealerDeviceFilterOptionsSchema,
  dealerDeviceListResponseSchema,
  dealerListResponseSchema,
  dealerProfileSchema,
  dealerUpdateResultSchema,
  usCitiesPageSchema,
} from './schemas';
import type {
  CreateDealerBody,
  DealerDetail,
  DealerDeviceAllocation,
  DealerDeviceFilterOptions,
  DealerDeviceFilters,
  DealerDeviceListResponse,
  DealerListFilters,
  DealerListResponse,
  DealerProfile,
  DealerToggleStatus,
  UpdateDealerBody,
  UsCity,
} from './types';

/** `<DataTable>` sorts by `dataIndex`; these two columns' fields differ from the BE's
 *  `DealerSortBy` enum. The other five match it as they are. */
const SORT_FIELD_MAP: Record<string, string> = { cityLabel: 'city', phone: 'contact' };

/** `GET /dealers/admin`. Unsorted sends no `sortBy` (the BE's own unsorted default, 118479 — there
 *  is no default sort). */
export async function listDealers({
  page,
  limit,
  search,
  sortBy,
  sortOrder,
  city,
  status,
}: ListParams<DealerListFilters>): Promise<DealerListResponse> {
  const { data, meta } = await apiClient.getList(endpoints.dealers.list, {
    // BE `ListDealersDto`: `city` is a REPEATED param (`city=a&city=b`, not axios's `city[]=`),
    // `status` is comma-separated. Empty → omitted.
    params: {
      page,
      limit,
      search,
      sortBy: sortBy && (SORT_FIELD_MAP[sortBy] ?? sortBy),
      sortOrder: sortOrder?.toUpperCase(),
      city: city?.length ? city : undefined,
      status: status?.length ? status.join(',') : undefined,
    },
    paramsSerializer: { indexes: null },
  });
  return dealerListResponseSchema.parse({ data, meta });
}

/** `GET /dealers/admin/:dealerId`. A removed Dealer is a 404 `DEALER_NOT_FOUND`. */
export async function getDealer(id: string): Promise<DealerProfile> {
  return dealerProfileSchema.parse(await apiClient.get(endpoints.dealers.detail(id)));
}

/** The device tab's wire query minus paging and sort. The list and the export both build from
 *  this, so the file holds exactly the set on screen (ADO 120974). */
export function dealerDeviceQuery(
  allocation: DealerDeviceAllocation,
  filters: DealerDeviceFilters,
) {
  return { allocation, ...dealerDeviceToListQuery(filters) };
}

/** `GET /dealers/admin/:dealerId/devices` for one allocation. */
export async function listDealerDevices(
  id: string,
  allocation: DealerDeviceAllocation,
  params: ListParams<DealerDeviceFilters>,
): Promise<DealerDeviceListResponse> {
  const { page, limit, sortBy, sortOrder, ...filters } = params;
  const { data, meta } = await apiClient.getList(endpoints.dealers.devices(id), {
    params: { page, limit, sortBy, sortOrder, ...dealerDeviceQuery(allocation, filters) },
  });
  return dealerDeviceListResponseSchema.parse({ data, meta });
}

/** `GET /dealers/admin/:dealerId/devices/meta/filters` — the drawer's options for one allocation. */
export async function getDealerDeviceFilterOptions(
  id: string,
  allocation: DealerDeviceAllocation,
): Promise<DealerDeviceFilterOptions> {
  return dealerDeviceFilterOptionsSchema.parse(
    await apiClient.get(endpoints.dealers.deviceFilters(id), { params: { allocation } }),
  );
}

/** `POST /dealers/admin` (BE §1.4). The response carries no toast copy; nothing reads its body. */
export async function createDealer(body: CreateDealerBody): Promise<void> {
  await apiClient.post(endpoints.dealers.create, body);
}

/** `GET /dealers/admin/meta/us-cities` (BE §1.3). The BE 400s outside 2–50 characters, so a shorter
 *  term outside that answers an empty page locally — no request, no options (AC: from the 2nd char). */
export async function listUsCities(p: {
  search: string;
  cursor?: string;
  limit: number;
}): Promise<CursorPage<UsCity>> {
  const search = p.search.trim();
  if (search.length < 2 || search.length > 50) return { data: [], hasNextPage: false };
  const res = await apiClient.getList(endpoints.dealers.usCities, {
    params: { search, limit: p.limit, cursor: p.cursor },
  });
  const { data, meta } = usCitiesPageSchema.parse(res);
  return { data, hasNextPage: meta.hasNextPage, nextCursor: meta.nextCursor ?? undefined };
}

/** `PATCH /dealers/admin/:dealerId/status` (BE L537-587). The response row is unread: the listing
 *  refetches. 409 `DEALER_PENDING_ACTIVATION` / 404 surface as `ApiError`. */
export async function setDealerStatus(id: string, status: DealerToggleStatus): Promise<void> {
  await apiClient.patch(endpoints.dealers.status(id), { status });
}

/** `DELETE /dealers/admin/:dealerId` (BE L589-632), soft delete. The response is unread: the listing
 *  refetches. 409 `DEALER_HAS_UNASSIGNED_DEVICES` (with `details` counts) / 404 surface as `ApiError`. */
export async function deleteDealer(id: string): Promise<void> {
  await apiClient.delete(endpoints.dealers.detail(id));
}

/** `getList`, not `get`: `get` unwraps `.data.data` and would drop the cursor `meta`. */
export async function listDealerCities(params: { search: string; cursor?: string; limit: number }) {
  const page = await apiClient.getList(endpoints.dealers.metaCities, {
    params: { search: params.search || undefined, cursor: params.cursor, limit: params.limit },
  });
  return dealerCityPageSchema.parse(page);
}

/** `GET /dealers/admin/:dealerId` (BE §1.5): the Edit prefill, fetched fresh on each open. Same endpoint as
 *  `getDealer`, read through the edit form's own schema. */
export async function getDealerForEdit(id: string): Promise<DealerDetail> {
  return dealerDetailSchema.parse(await apiClient.get(endpoints.dealers.detail(id)));
}

/** `PUT /dealers/admin/:dealerId` (BE §1.6). Answers `changed`: `false` means nothing was written,
 *  and the client suppresses the toast. 404 `DEALER_NOT_FOUND` / 409 `DEALER_STALE` and the
 *  uniqueness 409s surface as `ApiError`. */
export async function updateDealer(id: string, body: UpdateDealerBody): Promise<boolean> {
  return dealerUpdateResultSchema.parse(await apiClient.put(endpoints.dealers.detail(id), body))
    .changed;
}
